import { FC, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { IUser } from 'screens/Admin/RolesAndUsers/users.types'
import authAtom from 'screens/Auth/auth.atom'
import Card from 'shared/Cards/Card'
import TextField from 'shared/Forms/TextField'
import UserRolesDivider from '../UserRolesDivider'
import useHttpLoader from 'shared/hooks/useHttpLoader'
import usersApi from 'screens/Admin/RolesAndUsers/users.api'
import eventBus from 'services/eventBus'
import Button from 'shared/Buttons/Button/Button'
import userRolesConstants from 'screens/Users/constants/userRole'
import ActionsButtonWithMenu from 'shared/Buttons/ActionsButtonWithMenu'
import { useNavigate } from 'react-router-dom'

interface IProps {
  user: IUser
  onUpdated: (user: IUser) => void
}

const UserInfoCard: FC<IProps> = (props) => {
  const { wait, loading } = useHttpLoader()
  const navigate = useNavigate()

  const isAdmin = useRecoilValue(authAtom).user.isAdmin

  const [isEditing, setIsEditing] = useState(false)

  const [role, setUserRole] = useState<IUser['userRole']>(props.user.userRole)

  const handleOnClickEdit = () => {
    setIsEditing(!isEditing)
  }

  const handleChangeRole = (role: IUser['userRole']) => {
    setUserRole(role)
  }

  const handleDelete = () => {
    wait(usersApi.deleteUser(props?.user?.id), (resp) => {
      if (resp.status === 'success') {
        navigate('/admin/users')
        eventBus.pub('ntf.message', { type: 'info', message: 'Пользователь успешно удален' })
      }
    })
  }

  const handleSave = () => {
    wait(usersApi.updateUser({ userRole: role }, props.user.id), (resp) => {
      if (resp.status === 'success') {
        props.onUpdated(resp.body)
        eventBus.pub('ntf.message', { type: 'info', message: 'Роль успешно изменена' })
      }
    })
  }

  const isValid = role && props.user.userRole !== role

  const renderInfoCardContent = () => {
    const renderInfoField = (name: string, title: string) => {
      return <TextField header={title}>{props.user.info[name] || '-'}</TextField>
    }

    return (
      <div className='grid-form-2'>
        {renderInfoField('name', 'ФИО')}
        {renderInfoField('universityGroup', 'Универститетская группа')}
        {renderInfoField('phone', 'Номер телефона')}
        {renderInfoField('telegram', 'Telegram')}
        <TextField header='Роль'>
          {!isEditing && userRolesConstants.rolesMap[role]}
          {isEditing && <UserRolesDivider selectedRole={role} onChangeRole={handleChangeRole} />}
        </TextField>
      </div>
    )
  }

  return (
    <Card type='normal' style={{ width: '100%' }}>
      <div className='flex-lines'>
        <div className='flex-space-between'>
          <h2 className='text--caption-l text-700' style={{ marginBottom: '16px' }}>
            Общая информация
          </h2>
          {isAdmin && (
            <ActionsButtonWithMenu>
              <span className='options-contextmenu-option' onClick={handleOnClickEdit}>
                Редактировать
              </span>
              <span className='options-contextmenu-option' data-danger='true' onClick={handleDelete}>
                Удалить
              </span>
            </ActionsButtonWithMenu>
          )}
        </div>
        {renderInfoCardContent()}
      </div>
      {isEditing && (
        <div className='form-footer'>
          <div className='inline-flex-gap'>
            <Button
              theme='primary'
              style={{ width: 'fit-content' }}
              onClick={handleSave}
              loading={loading}
              disabled={!isValid}
            >
              Сохранить
            </Button>
            <Button
              theme='grey-outline'
              style={{ width: 'fit-content' }}
              onClick={() => {
                setIsEditing(false)
              }}
            >
              Отмена
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

export default UserInfoCard
